import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "~/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { isFeatureFlagEnabled } from "~/instrumentation/feature-flags";
import { useTRPC } from "~/lib/trpc";

export const Route = createFileRoute("/(app)/snapshot")({
  component: RouteComponent,
  beforeLoad: async ({}) => {
    const isAdminEnabled = isFeatureFlagEnabled("admin");
    if (!isAdminEnabled) {
      throw redirect({ to: "/" });
    }
  },
});

const formSchema = z.object({
  url: z.string().min(1, "URL is required"),
});

function RouteComponent() {
  const [snapshot, setSnapshot] = useState<string | null>(null);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      url: "",
    },
  });

  const trpc = useTRPC();

  const { data, isPending, mutate } = useMutation(
    trpc.pages.scrape.mutationOptions(),
  );

  const onSubmit = ({ url }: z.infer<typeof formSchema>) => {
    mutate(
      { url },
      {
        onSuccess: (data) => {
          setSnapshot(data.snapshot);
          if (data.isNew) {
            toast.success("Snapshot generated successfully");
          } else {
            toast.info("Snapshot updated");
          }
        },
      },
    );
  };

  const [iframeRef, setIframeRef] = useState<HTMLIFrameElement | null>(null);

  const handleIframeLoad = () => {
    if (iframeRef?.contentDocument) {
      const anchors = iframeRef.contentDocument.getElementsByTagName("a");
      console.log("Iframe loaded, found anchors:", anchors.length);
      for (let i = 0; i < anchors.length; i++) {
        anchors[i].onclick = (e) => {
          e.preventDefault();
          console.log(
            "Anchor click intercepted:",
            (e.target as HTMLAnchorElement).href,
          );
          return false;
        };
      }
    }
  };

  return (
    <div className="p-4">
      <div className="max-w-screen-2xl mx-auto flex flex-col gap-4 h-full">
        <div className="mb-4">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="url"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Enter URL to generate a snapshot</FormLabel>
                    <div className="flex items-center gap-2">
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="https://example.com"
                          disabled={isPending}
                        />
                      </FormControl>
                      <Button
                        type="submit"
                        disabled={isPending || !form.watch("url").trim()}
                      >
                        {isPending && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Submit
                      </Button>
                    </div>
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>
        {/* Full Screen Iframe */}
        <div className="flex flex-col mb-8 h-full">
          <div className="w-full flex-1 rounded-lg border">
            {data && !isPending ? (
              <iframe
                ref={setIframeRef}
                className="w-full h-full border-0 rounded-lg"
                srcDoc={snapshot || ""}
                sandbox="allow-scripts allow-same-origin"
                onLoad={handleIframeLoad}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
